import React, { useContext, useEffect, useState } from 'react';
import useForm from '../../hooks/form.hook';
import BackButtonLayout from '../../layouts/BackButtonLayout';
import { Camera, Eye, LucideSave, Pencil, X } from 'lucide-react';
import UserAvatar from '../../components/generic/UserAvatar';
import { AuthContext } from '../../contexts/auth.context';
import { useApi } from '../../hooks/api.hook';
import { ToasterContext } from '../../contexts/toaster.context';
import { IUser } from '../../shared/type/user.type';

export default function ProfilePage({userId}: {
    userId?: number;
}) {

    const auth = useContext(AuthContext);
    const userService = useApi<IUser>("user-account");
    const toaster = useContext(ToasterContext);

    const [editName, setEditName] = useState(false);
    const [user, _setUser] = useState<IUser|undefined>(undefined);
    function setUser(user: IUser|undefined) {
        _setUser(user);
        form.set({
            firstName: user?.profile.firstName || '',
            lastName: user?.profile.lastName || '',
            bio: user?.profile.bio || ''
        });
    }


    
    const form = useForm({
        avatar: '',
        firstName: '',
        lastName: '',
        bio: ''
    });

    function Save() {
        
        form.submit("auth/me", {
            method: "PATCH",
            body: {
                avatar: form.state.avatar || undefined,
                firstName: form.state.firstName || undefined,
                lastName: form.state.lastName || undefined,
                bio: form.state.bio || undefined
            },
            onSuccess: (data) => {
                auth.setUser(data);
                setEditName(false);
                toaster.makeToast("Profile updated");
            }
        });

    }

    function Crop(img: HTMLImageElement) {
        const _a = document.createElement('canvas');
        const a = _a.getContext('2d') as CanvasRenderingContext2D;
        const _b = document.createElement('canvas');
        const b = _b.getContext('2d') as CanvasRenderingContext2D;

        const size = Math.min(img.width, img.height);
        _a.width = size;
        _a.height = size;

        a.drawImage(img, Math.floor((img.width - size) / 2), Math.floor((img.height - size) / 2), size, size, 0, 0, size, size);

        if (_a.width <= 256 && _a.height <= 256) {
            form.set({ avatar: _a.toDataURL() });
            return;
        }

        while (_a.width/2 > 256) {
            _b.width = Math.floor(_a.width / 2);
            _b.height = _b.width;
            b.drawImage(_a, 0, 0, _a.width, _a.height, 0, 0, _b.width, _b.height);
            _a.width = _b.width;
            _a.height = _b.height;
            a.drawImage(_b, 0, 0);
        }

        _b.width = 256;
        _b.height = 256;
        b.drawImage(_a, 0, 0, _a.width, _a.height, 0, 0, _b.width, _b.height);
        form.set({ avatar: _b.toDataURL() });
    }

    function ChangeAvatar() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = false;

        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = async (e) => {
                const base64 = e.target?.result as string;
                const img = new Image();
                img.onload = () => {
                    Crop(img);
                }
                img.src = base64;
            }
            reader.readAsDataURL(file);
        };

        input.click();
    }


    useEffect(() => {
        if (!userId) {
            setUser(auth.user);
            return;
        }

        userService.get(userId.toString()).then((res) => {
            if (res.ok && res.data) {
                setUser(res.data);
            }
        });

    }, [
        userId
    ]);

    

    return (
        <BackButtonLayout title="Your profile"
        right={
            <div><button disabled={form.isLoading} onClick={Save} className="btn btn-ghost">{form.loadText(<LucideSave />)}</button></div>
        }
        className='border-b border-b-[#ffffff33]'>
            <div className="p-4 anim-slide-up mx-auto max-w-screen-md">
                <div>
                    <h1 className="text-3xl text-center mb-8">Customize how you're seen</h1>

                    <div className="bg-[#00000055] rounded-t-[64px] mx-4 p-4">
                        <div className="text-center">
                            <div className="inline-block relative">
                                {!form.state.avatar ? (
                                    <UserAvatar id={user?.id || 0} className='w-48' />
                                ) : (
                                    <div className="avatar"><div className="w-48 rounded-full bg-base-100"><img src={form.state.avatar} alt="Avatar" /></div></div>
                                )}
                                <button disabled={form.isLoading} onClick={ChangeAvatar} className="btn absolute bottom-4 right-4 btn-circle"><Camera /></button>
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-4 my-4">
                            {editName ? (
                                <>
                                <div>
                                    <input disabled={form.isLoading} id="firstName" name="firstName" type="text" placeholder='First Name'
                                    value={form.state.firstName} onChange={form.setFromInput}
                                    className='input input-sm' />
                                </div>
                                <div>
                                    <input disabled={form.isLoading} id="lastName" name="lastName" type="text" placeholder='Last Name'
                                    value={form.state.lastName} onChange={form.setFromInput}
                                    className='input input-sm' />
                                </div>
                                </>
                            ) : (
                                <>
                                <div>
                                    <p className="text-2xl">{form.state.firstName ? `${form.state.firstName} ${form.state.lastName || ""}` : user?.username}</p>
                                </div>
                                <div><button disabled={form.isLoading} onClick={() => setEditName(true)} className="btn btn-sm btn-ghost btn-circle"><Pencil size={16} /></button></div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="bg-[#00000055] border-t-4 border-base-300 rounded-b-[64px] mx-4 py-2 px-12">
                        <p className="text-xs opacity-50">Bio</p>
                        <textarea disabled={form.isLoading} name="bio" id="bio" placeholder='Tell us about yourself'
                        value={form.state.bio} onChange={form.setFromInput}
                        className='textarea bg-transparent w-full'></textarea>
                    </div>
                </div>

                
            </div>
        </BackButtonLayout>
    )
};